import type { SpecialityMatchType } from "@src/constants/filter-constants";
import { Layout } from "@uhg-abyss/web/ui/Layout";
import { SegmentedControls } from "@uhg-abyss/web/ui/SegmentedControls";
import { Text } from "@uhg-abyss/web/ui/Text";
import type { ComponentProps } from "react";

type CSS = NonNullable<ComponentProps<typeof Layout.Stack>["css"]>;

const DEFAULT_SPECIALITY_MATCH_TYPE: SpecialityMatchType = "or";

interface SpecialityMatchTypeToggleProps {
  value?: SpecialityMatchType;
  onChange: (value: SpecialityMatchType) => void;
  /** Prefix for the component's data-testid attributes. */
  testIdPrefix: string;
  css?: CSS;
}

/**
 * OR/AND toggle controlling how multiple selected "Reason for visit" values are
 * matched during provider search. Shared by the Get Started form and the
 * Find a Provider secondary filters.
 */
export const SpecialityMatchTypeToggle = ({
  value,
  onChange,
  testIdPrefix,
  css,
}: SpecialityMatchTypeToggleProps) => {
  const resolvedValue = value || DEFAULT_SPECIALITY_MATCH_TYPE;
  const helperText =
    resolvedValue === "and"
      ? "Providers must serve all reasons selected"
      : "Providers serve any of the reasons selected";

  return (
    <Layout.Stack css={{ gap: "8px", alignItems: "flex-start", ...css }}>
      <Text css={{ fontWeight: 500, fontSize: "14px" }}>{helperText}</Text>
      <SegmentedControls
        data-testid={`${testIdPrefix}-speciality-match-type-segmented-controls`}
        title="Reason for visit match type"
        value={resolvedValue}
        onChange={(selectedValue) => {
          onChange(selectedValue === "and" ? "and" : DEFAULT_SPECIALITY_MATCH_TYPE);
        }}
        css={{
          "abyss-segmented-controls-root": {
            ".abyss-segmented-controls-tab-label": {
              color: "$app.color.text.primary !important",
            },
            '[aria-selected="true"] .abyss-segmented-controls-tab-label': {
              color: "$app.color.text.primary.alt !important",
            },
            ".abyss-segmented-controls-selected-background": {
              backgroundColor: "$app.color.brand.primary !important",
              border: "1px solid $app.color.brand.primary !important",
            },
            "*::before": {
              display: "none !important",
              backgroundColor: "transparent !important",
              width: "0 !important",
            },
          },
        }}
      >
        <SegmentedControls.Tab
          id={`obc-${testIdPrefix}-speciality-match-type-segmented-controls-or-tab`}
          data-obc-label="Reason for Visit Match Type: Match Any"
          data-testid={`${testIdPrefix}-speciality-match-type-segmented-controls-or-tab`}
          label="Match Any"
          value="or"
        />
        <SegmentedControls.Tab
          id={`obc-${testIdPrefix}-speciality-match-type-segmented-controls-and-tab`}
          data-obc-label="Reason for Visit Match Type: Must Match All"
          data-testid={`${testIdPrefix}-speciality-match-type-segmented-controls-and-tab`}
          label="Must Match All"
          value="and"
        />
      </SegmentedControls>
    </Layout.Stack>
  );
};
